import type { PushAdapter, PushEvents, PushPermission } from './types'

interface Identity { installation_id: string; installation_secret: string }
interface Session { userId: number; bearer: string }
interface Dependencies {
  adapter: PushAdapter
  storage: Pick<Storage, 'getItem' | 'setItem'>
  post: (path: string, body: object, bearer: string) => Promise<unknown>
  events: Omit<PushEvents, 'token' | 'error'>
  changed?: () => void
}

export class PushService {
  permission: PushPermission = 'prompt'
  registered = false
  error = false
  private initialized?: Promise<void>
  private registration?: Promise<PushPermission>
  private session: Session | null = null
  private generation = 0
  private token = ''
  private acknowledged = ''
  private lastSubmission = 0
  private queue: Promise<void> = Promise.resolve()
  private retry?: ReturnType<typeof setTimeout>
  private retries = 0
  private enabled: boolean

  constructor(private deps: Dependencies) {
    this.enabled = deps.storage.getItem('push_enabled') === 'true'
  }

  identity(): Identity {
    const stored = this.deps.storage.getItem('push_installation')
    if (stored) {
      const parsed: Identity = JSON.parse(stored)
      if (/^[a-f0-9-]{36}$/.test(parsed.installation_id) && /^[a-f0-9]{64}$/.test(parsed.installation_secret)) return parsed
      throw new Error('Invalid stored push installation')
    }
    const identity = {
      installation_id: crypto.randomUUID(),
      installation_secret: Array.from(crypto.getRandomValues(new Uint8Array(32)), b => b.toString(16).padStart(2, '0')).join(''),
    }
    this.deps.storage.setItem('push_installation', JSON.stringify(identity))
    return identity
  }

  initialize(): Promise<void> {
    return this.initialized ??= this.deps.adapter.initialize({
      token: token => {
        if (this.token !== token) { this.cancelRetry(); this.retries = 0 }
        this.token = token
        void this.submit()
      },
      received: message => { if (this.session && this.enabled) this.deps.events.received(message) },
      tap: data => this.deps.events.tap(data),
      error: () => { this.error = true; this.changed() },
    }).catch(() => { this.initialized = undefined; this.error = true; this.changed() })
  }

  async authenticate(userId: number | null, bearer: string | null): Promise<void> {
    if (this.session?.userId === userId && this.session?.bearer === bearer) return
    if ((!userId || !bearer) && this.session) {
      await this.unregister(false).catch(() => { this.error = true; this.changed() })
      return
    }
    this.generation++
    this.cancelRetry()
    this.retries = 0
    this.session = userId && bearer ? { userId, bearer } : null
    this.registered = false
    this.acknowledged = ''
    await this.initialize()
    if (this.session && this.deps.adapter.platform !== 'web' && this.deps.storage.getItem('push_enabled') === null) {
      await this.register()
      return
    }
    await this.refresh()
  }

  async status(): Promise<PushPermission> {
    try { this.permission = await this.deps.adapter.permission() }
    catch { this.permission = 'unsupported' }
    this.changed()
    return this.permission
  }

  register(): Promise<PushPermission> {
    return this.registration ??= this.enable().finally(() => { this.registration = undefined })
  }

  private async enable(): Promise<PushPermission> {
    if (!this.session) throw new Error('Authentication required')
    const generation = this.generation
    await this.initialize()
    this.permission = await this.deps.adapter.requestPermission()
    if (generation !== this.generation) return this.permission
    this.enabled = this.permission === 'granted'
    this.deps.storage.setItem('push_enabled', String(this.enabled))
    this.error = false
    if (this.enabled) {
      this.identity()
      await this.deps.adapter.register()
      await this.submit()
    }
    this.changed()
    return this.permission
  }

  async refresh(): Promise<void> {
    if (!this.session || !this.enabled) return
    const generation = this.generation
    try {
      if (await this.status() !== 'granted') {
        await this.unregister()
        return
      }
      if (generation !== this.generation) return
      await this.deps.adapter.register()
      await this.submit()
    } catch { this.error = true; this.changed() }
  }

  private submit(): Promise<void> {
    const session = this.session
    const token = this.token
    const generation = this.generation
    if (!session || !token || !this.enabled) return Promise.resolve()
    this.queue = this.queue.then(async () => {
      if (generation !== this.generation) return
      const key = `${session.userId}:${session.bearer}:${token}`
      if (key === this.acknowledged && Date.now() - this.lastSubmission < 86400000) return
      if (this.retry) return
      try {
        await this.deps.post('/push/register', {
          ...this.identity(), platform: this.deps.adapter.platform, fcm_token: token,
          app_version: import.meta.env.VITE_APP_VERSION || undefined,
        }, session.bearer)
        if (generation !== this.generation) return
        this.acknowledged = key
        this.lastSubmission = Date.now()
        this.registered = true
        this.error = false
        this.retries = 0
        this.cancelRetry()
      } catch (error) {
        if (generation !== this.generation) return
        this.registered = false
        this.error = true
        const status = (error as { response?: { status?: number } }).response?.status
        if ((!status || status >= 500 || status === 429) && this.retries < 3 && !this.retry) {
          const delay = status === 429 ? 60000 : [1000, 5000, 15000][this.retries]!
          this.retries++
          this.retry = setTimeout(() => { this.retry = undefined; void this.submit() }, delay)
        }
      }
      this.changed()
    })
    return this.queue
  }

  async unregister(disable = true): Promise<void> {
    const session = this.session
    this.generation++
    this.cancelRetry()
    this.registered = false
    this.acknowledged = ''
    if (disable) {
      this.enabled = false
      this.deps.storage.setItem('push_enabled', 'false')
    }
    this.session = null
    // Await an in-flight registration before revoking it, so it cannot resurrect after logout.
    await this.queue
    let failure: unknown
    try {
      if (session && this.deps.storage.getItem('push_installation')) {
        await this.deps.post('/push/unregister', this.identity(), session.bearer)
      }
    } catch (error) { failure = error }
    try { await this.deps.adapter.unregister() }
    catch { this.error = true }
    this.token = ''
    if (disable) this.session = session
    this.changed()
    if (failure) throw new Error('Push unregistration failed')
  }

  async dispose(): Promise<void> {
    this.generation++
    this.cancelRetry()
    await this.queue
    await this.deps.adapter.dispose()
    this.initialized = undefined
  }

  private cancelRetry(): void { clearTimeout(this.retry); this.retry = undefined }
  private changed(): void { this.deps.changed?.() }
}
